import { Material } from '../../warehouse/material/material';
import { Behandlungsanfrage } from '../../documents/behandlungsanfrage/behandlungsanfrage';

export class Tourenplan extends Behandlungsanfrage {
    bestellung_id: number;
    bezeichnung: string;
    zieldatum: Date;
    zieldatum_bis: Date;
    status_id: number;
    status: string;
    apotheke: number;
    kunde_id: number;
    kunde_nr_intern: number;
    kunde: string;
    unternehmensgruppe_id: number;
    unternehmensgruppe: string;
    abweichende_lieferadresse: number;
    zusatz: string;
    strasse: string;
    strasse_lieferung: string;
    strasse_ug: string;
    plz: string;
    plz_lieferung: string;
    plz_ug: string;
    ort: string;
    ort_lieferung: string;
    ort_ug: string;
    ortsteil: string;
    ortsteil_lieferung: string;
    ortsteil_ug: string;
    telefon_1: string;
    mobil_1: string;
    tierarzt: string;
    tierarzt_id: number;
    tourenplan: number;
}

export interface TourenplanApi {
    records: Tourenplan[];
    count: number;
}

export class ArtikelMenge {
    article: Material;
    menge: number;
    identifikation: string;
    anzahl_tiere: number;
    ba_id: number;
}

/** Praktisches Limit; DB ist DECIMAL(12,4) max 99999999.9999 – Barcodes (EAN) landen sonst als Menge. */
export const MAX_TOURENPLAN_ARTIKELMENGE = 99999;

export function isValidTourenplanArtikelmenge(menge: unknown): boolean {
    const n = Number(menge);
    return Number.isFinite(n) && n > 0 && n <= MAX_TOURENPLAN_ARTIKELMENGE;
}

/**
 * Status for create / full edit (Artikel/Menge changed).
 * Kunde → 2 (aufgegeben), Tierarzt → 3 (freigegeben).
 * Header-only edits must not use this – they omit status_id.
 */
export function tourenplanSaveStatusId(isKunde: boolean): number {
    return isKunde ? 2 : 3;
}

function sameTourenplanDate(a: Date | string | null | undefined, b: Date | string | null | undefined): boolean {
    if (a == null && b == null) {
        return true;
    }
    if (a == null || b == null) {
        return false;
    }
    const da = new Date(a);
    const db = new Date(b);
    if (Number.isNaN(da.getTime()) || Number.isNaN(db.getTime())) {
        return String(a).slice(0, 10) === String(b).slice(0, 10);
    }
    return da.toISOString().slice(0, 10) === db.toISOString().slice(0, 10);
}

function sameTourenplanPositionen(tour: Tourenplan, artikelMenge: ArtikelMenge[]): boolean {
    const orig = tour.positionen ?? [];
    if (orig.length !== artikelMenge.length) {
        return false;
    }
    for (let i = 0; i < orig.length; i++) {
        const p = orig[i];
        const a = artikelMenge[i];
        if (Number(p.artikel_id) !== Number(a.article?.artikel_id)) {
            return false;
        }
        if (Number(p.artikelmenge) !== Number(a.menge)) {
            return false;
        }
        if (String(p.identifikation ?? '').trim() !== String(a.identifikation ?? '').trim()) {
            return false;
        }
        if (Number(p.anzahl_tiere ?? 0) !== Number(a.anzahl_tiere ?? 0)) {
            return false;
        }
        if (Number(p.ba_id ?? 0) !== Number(a.ba_id ?? 0)) {
            return false;
        }
    }
    return true;
}

/**
 * Positions unchanged + at least one header field changed (Notiz, Datum,
 * Apotheke, Kunde/UG). Artikel/Menge edits must use the full save path.
 */
export function isTourenplanCommentOnlyEdit(
    tour: Tourenplan | null | undefined,
    notiz: string | null | undefined,
    zieldatumFrom: Date | string | null | undefined,
    zieldatumTo: Date | string | null | undefined,
    apotheke: boolean,
    artikelMenge: ArtikelMenge[],
    kundeId?: number | null,
    unternehmensgruppeId?: number | null,
): boolean {
    if (!tour?.bestellung_id) {
        return false;
    }
    if (!sameTourenplanPositionen(tour, artikelMenge)) {
        return false;
    }
    const notizChanged =
        String(notiz ?? '').trim() !== String(tour.bezeichnung ?? '').trim();
    const dateFromChanged = !sameTourenplanDate(zieldatumFrom, tour.zieldatum);
    const dateToChanged = !sameTourenplanDate(zieldatumTo, tour.zieldatum_bis);
    const apothekeChanged = Number(apotheke ? 1 : 0) !== Number(tour.apotheke ?? 0);
    const origKunde = tour.kunde_id != null ? Number(tour.kunde_id) : null;
    const origUg =
        tour.unternehmensgruppe_id != null ? Number(tour.unternehmensgruppe_id) : null;
    const nextKunde = kundeId != null && kundeId > 0 ? Number(kundeId) : null;
    const nextUg =
        unternehmensgruppeId != null && unternehmensgruppeId > 0
            ? Number(unternehmensgruppeId)
            : null;
    const kundeChanged = nextKunde !== origKunde || nextUg !== origUg;
    return (
        notizChanged ||
        dateFromChanged ||
        dateToChanged ||
        apothekeChanged ||
        kundeChanged
    );
}

/** Header-only update – no status_id / positionen (keeps Kommissioniert etc.). */
export function buildTourenplanCommentOnlyPayload(args: {
    bestellungId: number;
    notiz: string | null | undefined;
    zieldatum?: Date | string | null;
    zieldatumBis?: Date | string | null;
    apotheke?: boolean;
    kundeId?: number | null;
    unternehmensgruppeId?: number | null;
}): Partial<Behandlungsanfrage> & { bestellung_id: number; bezeichnung: string } {
    const out: Partial<Behandlungsanfrage> & {
        bestellung_id: number;
        bezeichnung: string;
    } = {
        bestellung_id: args.bestellungId,
        bezeichnung: args.notiz ?? '',
        zieldatum: (args.zieldatum as any) ?? null,
        zieldatum_bis: (args.zieldatumBis as any) ?? null,
        apotheke: args.apotheke ? 1 : 0,
    };
    const kundeId =
        args.kundeId != null && args.kundeId > 0 ? Number(args.kundeId) : null;
    const ugId =
        args.unternehmensgruppeId != null && args.unternehmensgruppeId > 0
            ? Number(args.unternehmensgruppeId)
            : null;
    if (kundeId != null) {
        out.kunde_id = kundeId;
    } else if (ugId != null) {
        out.unternehmensgruppe_id = ugId;
    }
    return out;
}
